import { Check, Sparkles, ArrowUpRight } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { EbookSection } from '../sections/EbookSection';
import { BankPayment } from '../components/BankPayment';
import { SectionHeading } from '../components/SectionHeading';
import { Reveal } from '../components/Reveal';
import { CTASection } from '../components/CTASection';
import { courses } from '../data/content';
import { site, img } from '../data/site';
import { usePageMeta } from '../components/ScrollToTop';

const individualCourses = [
  'Ads Targeting & Conversion',
  'AI Prompt Engineering',
  'Social Media Marketing',
  'Google Ads',
  'Search Engine Optimization (SEO)',
];

export default function Courses() {
  usePageMeta(
    'Digital Marketing Courses — TheBigHub | Learn Skills. Unlock Opportunities.',
    'Practical digital marketing courses from TheBigHub — Complete Digital Marketing Course ₦150,000, Google Ads & SEO Mastery ₦100,000, individual courses ₦50,000 each.'
  );
  return (
    <main data-testid="courses-page">
      <PageHero
        eyebrow="Digital Marketing Courses & Ebooks"
        title="Invest in Skills That Create Opportunities."
        copy="Access practical digital marketing courses and learning resources designed to help you understand digital marketing, develop valuable skills, and apply them to real business challenges. Learn at your own pace or explore structured training opportunities."
        image="/learn-virtual.jpg"
        imageAlt="Young African woman learning digital skills on her laptop in a virtual class"
      />
      <EbookSection />

      <section className="bg-cream">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
          <SectionHeading
            chapter="01"
            eyebrow="Featured Courses"
            title="Build Skills That Move You Forward."
          />
          <div className="mt-14 grid gap-5 lg:grid-cols-3">
            {courses.map((c, i) => {
              const formUrl = site.courseFormUrl;
              return (
                <Reveal key={c.slug} delay={i * 0.1} className="h-full">
                  <div
                    data-testid={`courses-page-card-${c.slug}`}
                    className={`card-lift flex h-full flex-col rounded-2xl p-8 ${
                      c.featured
                        ? 'grain relative overflow-hidden border-2 border-forest bg-forest text-white shadow-[0_36px_80px_-32px_rgba(0,59,36,0.6)]'
                        : 'border-2 border-forest bg-forest'
                    }`}
                  >
                    <span
                      className={`mb-6 inline-flex w-fit rounded-full px-3.5 py-1 font-mono text-[10px] uppercase tracking-[0.22em] ${
                        c.featured ? 'bg-white text-forest' : 'bg-white/15 text-goldlight'
                      }`}
                    >
                      {c.badge}
                    </span>
                    <h3 className={`font-display text-2xl font-bold leading-snug text-white`}>
                      {c.name}
                    </h3>
                    <div className="mt-4 flex items-baseline gap-3">
                      <span className={`font-display text-4xl font-bold text-goldlight`}>
                        {c.price}
                      </span>
                      {c.priceSuffix && (
                        <span className={`text-sm text-white`}>{c.priceSuffix}</span>
                      )}
                      {c.oldPrice && (
                        <span className={`text-sm line-through text-white`}>
                          {c.oldPrice}
                        </span>
                      )}
                    </div>
                    <p className={`mt-4 text-sm leading-relaxed text-white`}>
                      {c.description}
                    </p>
                    <ul className="mt-6 flex-1 space-y-2.5">
                      {c.points.map((p) => (
                        <li key={p} className={`flex items-start gap-2.5 text-sm text-white`}>
                          <Check size={15} className={`mt-0.5 shrink-0 text-goldlight`} strokeWidth={3} />
                          {p}
                        </li>
                      ))}
                    </ul>
                    <a
                      href={formUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`courses-enroll-${c.slug}`}
                      className="btn-mint mt-8 w-full"
                    >
                      Register — {c.name} <ArrowUpRight size={16} />
                    </a>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.15}>
            <div
              data-testid="individual-courses-panel"
              className="mt-16 rounded-2xl border-2 border-forest bg-forest p-8 sm:p-10"
            >
              <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-center">
                <div className="max-w-xl">
                  <p className="eyebrow mb-4 flex items-center gap-2 text-goldlight">
                    <Sparkles size={14} /> Individual Courses — ₦50,000 Each
                  </p>
                  <h3 className="font-display text-2xl font-bold text-white sm:text-3xl">
                    Pick the Exact Skill You Need.
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-white">
                    Every individual digital marketing course costs ₦50,000. Explore and select the
                    course that matches your goals — including our in-demand Ads Targeting &
                    Conversion and AI Prompt Engineering courses.
                  </p>
                </div>
                <ul className="grid flex-1 gap-3 sm:grid-cols-2">
                  {individualCourses.map((c) => (
                    <li
                      key={c}
                      className="flex items-center gap-3 rounded-xl border border-white/20 bg-white/10 px-4 py-3 text-sm font-semibold text-white"
                    >
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-goldlight" aria-hidden="true" />
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-10">
              <BankPayment />
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection
        title="Ready to Learn? Let's Talk."
        copy="Tell us which course you're interested in and we'll share the next steps. Questions first? Message us on WhatsApp — we're happy to help you choose."
      />
    </main>
  );
}
